import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { SegmentedControl } from '@workspace/ui/components/SegmentedControl';

import { DeliveryMethods } from '~/api/ETA';
import { useDeliveryMethodSwitch } from '~/hooks/useDeliveryMethodSwitch';
import { checkoutStore, deliveryMethodAvailabilityStore } from '~/stores';

/**
 * Delivery/Pickup switcher for the mobile header. Renders only when both
 * courier and pickup are available at the current point (per the store-owned
 * ETA probes). Toggling goes through useDeliveryMethodSwitch with
 * gateOnSavedAddress: a cross-warehouse switch is deferred to the
 * cart-clearing confirmation modal; otherwise the switch is deferred to the
 * saved-address-or-new-address modal, and only commits once an address is
 * confirmed there. The store reaction then recalculates ETA and, when the
 * warehouse changes, the catalog. Captions reuse the existing address-modal
 * toggle i18n keys.
 */
export const HeaderDeliverySwitcher = observer(() => {
  const { t } = useTranslation();
  const onDeliveryMethodChange = useDeliveryMethodSwitch({
    gateOnSavedAddress: true,
  });

  if (!deliveryMethodAvailabilityStore.isSwitcherVisible) {
    return null;
  }

  return (
    <div className="w-full px-4">
      <SegmentedControl<DeliveryMethods>
        variant="navigation"
        value={checkoutStore.deliveryMethod}
        onChange={onDeliveryMethodChange}
        options={[
          {
            value: DeliveryMethods.COURIER_DELIVERY,
            label: t('checkout:address:tabDelivery'),
            description: deliveryMethodAvailabilityStore.courierEtaRange
              ? t('phrases:inMinutes', {
                  time: deliveryMethodAvailabilityStore.courierEtaRange,
                })
              : undefined,
          },
          {
            value: DeliveryMethods.CLICK_AND_COLLECT,
            label: t('checkout:address:tabPickup'),
            description: deliveryMethodAvailabilityStore.pickupEtaRange
              ? t('phrases:inMinutes', {
                  time: deliveryMethodAvailabilityStore.pickupEtaRange,
                })
              : undefined,
          },
        ]}
      />
    </div>
  );
});

HeaderDeliverySwitcher.displayName = 'HeaderDeliverySwitcher';
